// ============================================================================
// PROJECT OVERVIEW TAB
// Purpose-built overview for item_type === 'project'.
// Shows: health/timeline header, financial snapshot, basic info, client/contact,
//        jobs summary, and inline subtasks.
// Uses the /api/v1/projects/{id}/financial-summary API for live data.
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Target, DollarSign, Clock, Briefcase, Calendar, TrendingUp, TrendingDown,
  FileText, Building2, MapPin, User, Search, ChevronDown, AlertCircle,
  BarChart3, Receipt, Percent, Loader2, Users, Flag
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import InlineSubtasksSection from '../work-items/InlineSubtasksSection';

// ── Types ──

interface ProjectOverviewTabProps {
  workItem: WorkItem;
  formData: Record<string, any>;
  onDataChange: (field: string, value: any) => void;
  isDark?: boolean;
  workItemId?: string;
  onOpenItem?: (itemId: string) => void;
  onOpenSubtask?: (itemId: string) => void;
  onSubtasksCountChange?: (count: number) => void;
}

interface FinancialSnapshot {
  budget: { estimated: number; actual: number; variance: number; utilization_percent: number };
  time: { total_hours: number; billable_hours: number; billable_ratio: number };
  revenue: { invoiced: number; quoted: number };
  jobs: { total: number; completed: number; in_progress: number; scheduled: number };
  health: string;
}

interface Contact {
  id: number;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  contact_type: string;
}

interface Location {
  id: number;
  contact_id: number;
  location_name: string;
  address_line1: string | null;
  city: string | null;
  is_primary: boolean;
}

// ── Helpers ──

const fmt = (n: number) => `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCompact = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `R ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `R ${(n / 1_000).toFixed(1)}K`;
  return fmt(n);
};

const getToken = () => authService.getTokens()?.access_token || '';

// ── Component ──

export default function ProjectOverviewTab({
  workItem, formData, onDataChange, isDark: isDarkProp,
  workItemId, onOpenItem, onOpenSubtask, onSubtasksCountChange
}: ProjectOverviewTabProps) {
  const { currentTheme } = useTheme();
  const isDark = isDarkProp ?? currentTheme.mode === 'dark';
  const effectiveId = workItemId || workItem.id;

  // Financial data
  const [financial, setFinancial] = useState<FinancialSnapshot | null>(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);

  // Contact state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const contactRef = useRef<HTMLDivElement>(null);

  // Locations
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // ── Fetch Financial Summary ──
  const fetchFinancial = useCallback(async () => {
    if (!effectiveId) return;
    setLoadingFinancial(true);
    try {
      const res = await fetch(`/api/v1/projects/${effectiveId}/financial-summary`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const j = await res.json();
        if (j.success) {
          const d = j.data;
          const utilPct = d.variance?.total_budget ? (d.variance.total_actual / d.variance.total_budget) * 100 : 0;
          setFinancial({
            budget: {
              estimated: d.project?.estimated_cost || d.variance?.total_budget || 0,
              actual: d.project?.actual_cost || d.variance?.total_actual || 0,
              variance: d.variance?.budget_variance || 0,
              utilization_percent: utilPct,
            },
            time: {
              total_hours: d.time_summary?.total_hours || 0,
              billable_hours: d.time_summary?.billable_hours || 0,
              billable_ratio: d.time_summary?.total_hours ? (d.time_summary.billable_hours / d.time_summary.total_hours) * 100 : 0,
            },
            revenue: {
              invoiced: d.billing_summary?.invoiced_amount || 0,
              quoted: d.billing_summary?.total_billing || 0,
            },
            jobs: {
              total: d.jobs_summary?.total_jobs || 0,
              completed: d.jobs_summary?.completed_jobs || 0,
              in_progress: d.jobs_summary?.in_progress_jobs || 0,
              scheduled: 0,
            },
            health: d.variance?.is_over_budget ? 'at_risk' : 'on_track',
          });
        }
      }
    } catch (err) { console.error('Failed to fetch financial data', err); }
    finally { setLoadingFinancial(false); }
  }, [effectiveId]);

  useEffect(() => { fetchFinancial(); }, [fetchFinancial]);

  // ── Load Contacts ──
  useEffect(() => {
    (async () => {
      setLoadingContacts(true);
      try {
        const res = await fetch('/api/v1/contacts?contact_types=customer,supplier&limit=200', {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.ok) { const d = await res.json(); if (d.success) setContacts(d.data || []); }
      } catch {} finally { setLoadingContacts(false); }
    })();
  }, []);

  // ── Load Locations when contact changes ──
  useEffect(() => {
    if (!formData.contact_id) { setLocations([]); return; }
    (async () => {
      setLoadingLocations(true);
      try {
        const res = await fetch(`/api/v1/contacts/${formData.contact_id}/locations`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.ok) { const d = await res.json(); if (d.success) setLocations(d.data || []); }
      } catch {} finally { setLoadingLocations(false); }
    })();
  }, [formData.contact_id]);

  // Click outside to close contact dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) setIsContactOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleContactChange = (contactId: number | null, contactName?: string) => {
    onDataChange('contact_id', contactId);
    onDataChange('contact_name', contactName || null);
    onDataChange('location_id', null);
    onDataChange('location_name', null);
  };

  const handleLocationChange = (locationId: number | null) => {
    const loc = locations.find(l => l.id == locationId);
    onDataChange('location_id', locationId);
    onDataChange('location_name', loc?.location_name || null);
  };

  const selectedContact = contacts.find(c => c.id == formData.contact_id);
  const filteredContacts = contacts.filter(c => {
    const name = `${c.company_name || ''} ${c.contact_person || ''} ${c.email || ''}`.toLowerCase();
    return name.includes(contactSearch.toLowerCase());
  });

  // ── Timeline Calculations ──
  const startDate = formData.start_date ? new Date(formData.start_date) : null;
  const dueDate = formData.due_date ? new Date(formData.due_date) : null;
  const now = new Date();
  const daysRemaining = dueDate ? Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  const totalDays = startDate && dueDate ? Math.ceil((dueDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const elapsedDays = startDate ? Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const timelineProgress = totalDays && elapsedDays ? Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100))) : 0;

  const healthColors: Record<string, { bg: string; text: string; label: string }> = {
    on_track: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'On Track' },
    needs_attention: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', label: 'Needs Attention' },
    at_risk: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'At Risk' },
    completed: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Completed' },
  };

  return (
    <div className="space-y-4">
      {/* ═══════════════════ PROJECT HEALTH & TIMELINE ═══════════════════ */}
      <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Progress Ring */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="40" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth="8" fill="none" />
                <circle cx="48" cy="48" r="40"
                  stroke={
                    (formData.progress_percentage || 0) === 100 ? '#10b981' :
                    (formData.progress_percentage || 0) >= 75 ? '#3b82f6' :
                    (formData.progress_percentage || 0) >= 50 ? '#f59e0b' :
                    (formData.progress_percentage || 0) >= 25 ? '#f97316' : '#ef4444'
                  }
                  strokeWidth="8" fill="none" strokeLinecap="round"
                  strokeDasharray={`${(formData.progress_percentage || 0) * 2.51} 251`}
                  className="transition-all duration-500 ease-out" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.progress_percentage || 0}%</span>
              </div>
            </div>
            <div className="flex gap-1 mt-2">
              {[0, 25, 50, 75, 100].map(val => (
                <button key={val} onClick={() => onDataChange('progress_percentage', val)}
                  className={`px-1.5 py-0.5 text-xs rounded transition-all ${
                    (formData.progress_percentage || 0) === val
                      ? 'bg-blue-500 text-white'
                      : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>{val}%</button>
              ))}
            </div>
          </div>

          {/* Health & Timeline */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              {financial && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${healthColors[financial.health]?.bg || 'bg-gray-100'} ${healthColors[financial.health]?.text || 'text-gray-700'}`}>
                  <span className={`w-2 h-2 rounded-full ${financial.health === 'on_track' ? 'bg-green-500' : financial.health === 'at_risk' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                  {healthColors[financial.health]?.label || 'Unknown'}
                </span>
              )}
              {daysRemaining !== null && (
                <span className={`text-xs font-medium ${isOverdue ? 'text-red-500' : daysRemaining <= 7 ? 'text-yellow-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {isOverdue ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days remaining`}
                </span>
              )}
            </div>

            {/* Date Inputs */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Calendar className="w-3 h-3 inline mr-1" />Start Date
                </label>
                <input type="date" value={formData.start_date ? formData.start_date.substring(0, 10) : ''}
                  onChange={(e) => onDataChange('start_date', e.target.value || null)}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <Flag className="w-3 h-3 inline mr-1" />Due Date
                </label>
                <input type="date" value={formData.due_date ? formData.due_date.substring(0, 10) : ''}
                  onChange={(e) => onDataChange('due_date', e.target.value || null)}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`} />
              </div>
            </div>

            {/* Timeline Progress Bar */}
            {startDate && dueDate && (
              <div>
                <div className={`relative h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  {/* Time elapsed bar */}
                  <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${isOverdue ? 'bg-red-500' : 'bg-blue-400/50'}`}
                    style={{ width: `${timelineProgress}%` }} />
                  {/* Progress bar (overlays) */}
                  <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
                    (formData.progress_percentage || 0) >= timelineProgress ? 'bg-green-500' : 'bg-orange-500'
                  }`} style={{ width: `${formData.progress_percentage || 0}%` }} />
                </div>
                <div className="flex justify-between mt-1 text-[10px]">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                    {startDate.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                    {(formData.progress_percentage || 0) >= timelineProgress ? '✓ Ahead of schedule' : '⚠ Behind schedule'}
                  </span>
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                    {dueDate.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════ FINANCIAL SNAPSHOT ═══════════════════ */}
      {(loadingFinancial || financial) && (
        <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <DollarSign className="w-4 h-4 text-green-500" />
              Financial Snapshot
            </h3>
          </div>
          {loadingFinancial ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            </div>
          ) : financial && (
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Budget', value: fmtCompact(financial.budget.estimated), icon: DollarSign, color: 'blue' },
                  { label: 'Actual', value: fmtCompact(financial.budget.actual), icon: BarChart3, color: 'purple' },
                  { label: 'Variance', value: `${financial.budget.variance >= 0 ? '+' : ''}${fmtCompact(financial.budget.variance)}`, icon: financial.budget.variance > 0 ? TrendingUp : TrendingDown, color: financial.budget.variance > 0 ? 'red' : 'green' },
                  { label: 'Utilization', value: `${financial.budget.utilization_percent.toFixed(0)}%`, icon: Percent, color: 'amber' },
                  { label: 'Hours', value: `${financial.time.total_hours.toFixed(1)}h`, icon: Clock, color: 'sky' },
                  { label: 'Billable', value: `${financial.time.billable_ratio.toFixed(0)}%`, icon: Clock, color: 'cyan' },
                  { label: 'Invoiced', value: fmtCompact(financial.revenue.invoiced), icon: Receipt, color: 'emerald' },
                  { label: 'Jobs', value: `${financial.jobs.completed}/${financial.jobs.total}`, icon: Briefcase, color: 'indigo' },
                ].map(card => {
                  const Icon = card.icon;
                  return (
                    <div key={card.label} className={`p-3 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</span>
                        <Icon className={`w-3.5 h-3.5 text-${card.color}-500`} />
                      </div>
                      <div className={`text-base font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{card.value}</div>
                    </div>
                  );
                })}
              </div>

              {/* Budget Alert */}
              {financial.budget.variance > 0 && financial.budget.utilization_percent > 100 && (
                <div className={`mt-3 p-3 rounded-lg border flex items-start gap-2 ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                  <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                  <div className={`text-xs ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                    <strong>Over budget</strong> by {fmtCompact(financial.budget.variance)} — utilization at {financial.budget.utilization_percent.toFixed(0)}%.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ BASIC INFO + CLIENT ═══════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Information */}
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <FileText className="w-4 h-4" />
            Project Details
          </h3>
          <div className="space-y-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Title <span className="text-red-500">*</span>
              </label>
              <input type="text" value={formData.title || ''} onChange={(e) => onDataChange('title', e.target.value)}
                className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Project name" />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
              <textarea value={formData.description || ''} onChange={(e) => onDataChange('description', e.target.value)} rows={5}
                className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-y min-h-[100px] ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Project scope and objectives" />
            </div>
            {/* Tags */}
            {workItem.tags && workItem.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {workItem.tags.map((tag: string, i: number) => (
                  <span key={i} className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Client & Contact */}
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Building2 className="w-4 h-4" />
            Client
          </h3>

          {/* Contact Selector */}
          <div className="relative mb-3" ref={contactRef}>
            <button type="button" onClick={() => setIsContactOpen(!isContactOpen)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className={`truncate ${!selectedContact ? 'text-gray-400' : ''}`}>
                  {selectedContact ? (selectedContact.company_name || selectedContact.contact_person) : 'Select client...'}
                </span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${isContactOpen ? 'rotate-180' : ''}`} />
            </button>
            {isContactOpen && (
              <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input type="text" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search contacts..."
                      className={`w-full pl-7 pr-2 py-1 text-sm rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200'}`} autoFocus />
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {loadingContacts ? <div className="p-2 text-center text-gray-500 text-xs">Loading...</div> :
                  filteredContacts.length === 0 ? <div className="p-2 text-center text-gray-500 text-xs">No contacts found</div> :
                  filteredContacts.map(c => (
                    <button key={c.id} type="button"
                      onClick={() => { handleContactChange(c.id, c.company_name || c.contact_person || undefined); setIsContactOpen(false); setContactSearch(''); }}
                      className={`w-full flex items-center gap-2 p-2 text-left text-sm ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${formData.contact_id == c.id ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}>
                      <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{c.company_name || c.contact_person || 'Unnamed'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Contact Info Display */}
          {selectedContact && (
            <div className={`p-3 rounded-lg mb-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                {selectedContact.company_name || selectedContact.contact_person}
              </div>
              {selectedContact.email && (
                <div className={`flex items-center gap-1 text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>📧</span> {selectedContact.email}
                </div>
              )}
              {selectedContact.phone && (
                <div className={`flex items-center gap-1 text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>📞</span> {selectedContact.phone}
                </div>
              )}
            </div>
          )}

          {/* Location Selector */}
          {formData.contact_id && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <MapPin className="w-3 h-3 inline mr-1" />Location
              </label>
              {loadingLocations ? (
                <div className={`text-xs p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading locations...</div>
              ) : locations.length === 0 ? (
                <div className={`text-xs p-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No locations on file</div>
              ) : (
                <select value={formData.location_id || ''}
                  onChange={(e) => handleLocationChange(e.target.value ? parseInt(e.target.value) : null)}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}>
                  <option value="">Select location...</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.location_name}{l.is_primary ? ' ★' : ''}{l.address_line1 ? ` — ${l.address_line1}` : ''}{l.city ? `, ${l.city}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════ JOBS OVERVIEW ═══════════════════ */}
      {financial && financial.jobs.total > 0 && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Briefcase className="w-4 h-4 text-blue-500" />
            Jobs Overview
          </h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className={`text-center p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{financial.jobs.total}</div>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Jobs</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
              <div className="text-2xl font-bold text-green-500">{financial.jobs.completed}</div>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Completed</div>
            </div>
            <div className={`text-center p-3 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <div className="text-2xl font-bold text-blue-500">{financial.jobs.in_progress}</div>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>In Progress</div>
            </div>
          </div>
          {/* Job completion bar */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Job Completion
              </span>
              <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {financial.jobs.total > 0 ? Math.round((financial.jobs.completed / financial.jobs.total) * 100) : 0}%
              </span>
            </div>
            <div className={`h-2.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${financial.jobs.total > 0 ? (financial.jobs.completed / financial.jobs.total) * 100 : 0}%` }} />
            </div>
          </div>

          {/* Team Summary */}
          {financial.time.total_hours > 0 && (
            <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between text-xs">
                <span className={`flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Users className="w-3 h-3" /> Team Effort
                </span>
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  {financial.time.total_hours.toFixed(1)}h total · {financial.time.billable_hours.toFixed(1)}h billable ({financial.time.billable_ratio.toFixed(0)}%)
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ SUBTASKS ═══════════════════ */}
      <InlineSubtasksSection
        workItemId={effectiveId}
        workItemType="project"
        isDark={isDark}
        onOpenItem={onOpenItem}
        onOpenSubtask={onOpenSubtask}
        onSubtasksCountChange={onSubtasksCountChange}
      />
    </div>
  );
}
